/**
 * The timeline scrubber's navigable geometry, as pure functions.
 *
 * Extracted from the component because THIS is where the scrubber's bugs
 * lived, and none of them were reachable from a test while the maths sat in
 * render closures. The track used to place ticks, the cursor and the tooltip
 * with three different expressions — evenly-spaced `i / max` for plain ticks
 * and the cursor, true-date scaling for highlight strokes, and `Math.round(f *
 * max)` for click resolution. They disagreed, and every symptom (a click that
 * navigates elsewhere, a cursor that lands off the tick you clicked, a date
 * that doesn't match the track) was one of those disagreements surfacing.
 *
 * Here there is ONE position function and ONE resolver, both keyed on the date
 * of the snapshot a stop actually navigates to, so "hover, click, cursor and
 * content agree" is a property a test can assert rather than a convention the
 * next edit has to remember.
 */

/** The axis entry fields this module needs — structurally typed so the tests
 *  don't have to build whole `TimelineHighlight` objects. */
export interface StopSnapshot {
  date: string;
  highlight?: { at?: string | null } | null;
}

export interface StopAxis {
  /** Navigable snapshot indices, ascending. Always a subset of the input. */
  stops: number[];
  /** Where snapshot `i` sits along the track, 0..1. */
  posFrac(i: number): number;
  /** The stop nearest `x` px into a track `width` px wide. */
  nearestStop(x: number, width: number): number | null;
  /** Dates of the first / last stop — the track's two end labels. */
  firstDate: string | undefined;
  lastDate: string | undefined;
}

/**
 * Which snapshots the scrubber can land on: every ERA (an entry carrying a
 * narrative highlight), plus the newest snapshot.
 *
 * The newest is included because the Timeline LANDS there — `index` defaults to
 * the last axis entry, whose payload is embedded in the axis and so renders
 * without a fetch. When the most recent snapshot carries no highlight (the
 * common case: eras are derived over history, "now" is just the latest tick),
 * omitting it would strand the default selection off-track and make the present
 * unreachable after a single click.
 *
 * An axis with NO highlights at all — an org whose eras haven't been derived —
 * falls back to every snapshot. Degraded, but navigable, rather than a track
 * with a single tick on it.
 */
export function eraStops(snapshots: ReadonlyArray<StopSnapshot>): number[] {
  if (snapshots.length === 0) return [];
  const lastIndex = snapshots.length - 1;
  const eras = snapshots.reduce<number[]>((acc, s, i) => {
    if (s.highlight) acc.push(i);
    return acc;
  }, []);
  if (eras.length === 0) return snapshots.map((_, i) => i);
  if (eras[eras.length - 1] !== lastIndex) eras.push(lastIndex);
  return eras;
}

/**
 * Build the track geometry for an axis.
 *
 * Positions are scaled across the STOP RANGE (first stop → last stop), not the
 * full snapshot range, so the leftmost tick sits at 0 and the rightmost at 1 —
 * which is what lets the two end labels, which read those same two dates,
 * actually bracket the track instead of floating over dead air before the
 * earliest era.
 *
 * Degenerate inputs (no stops, a single stop, unparseable dates, a zero-width
 * range) collapse to 0 rather than producing NaN offsets that would throw the
 * ticks off-screen.
 */
export function buildStopAxis(snapshots: ReadonlyArray<StopSnapshot>): StopAxis {
  const stops = eraStops(snapshots);
  const first = stops[0];
  const last = stops[stops.length - 1];
  const firstDate = first != null ? snapshots[first]?.date : undefined;
  const lastDate = last != null ? snapshots[last]?.date : undefined;
  const minT = Date.parse(firstDate ?? '');
  const maxT = Date.parse(lastDate ?? '');
  const usable = !Number.isNaN(minT) && !Number.isNaN(maxT) && maxT > minT;

  const posFrac = (i: number): number => {
    if (!usable) return 0;
    const t = Date.parse(snapshots[i]?.date ?? '');
    if (Number.isNaN(t)) return 0;
    return Math.min(1, Math.max(0, (t - minT) / (maxT - minT)));
  };

  const nearestStop = (x: number, width: number): number | null => {
    if (stops.length === 0) return null;
    let best: number | null = null;
    let bestDist = Infinity;
    for (const s of stops) {
      // No proximity cap and no fall-through: every x resolves to a stop. The
      // old code capped detection at 9px and otherwise fell back to an index
      // computed from even spacing, which is how a click a few pixels off a
      // stroke landed on an unrelated snapshot.
      const dist = Math.abs(x - posFrac(s) * width);
      if (dist < bestDist) {
        bestDist = dist;
        best = s;
      }
    }
    return best;
  };

  return { stops, posFrac, nearestStop, firstDate, lastDate };
}

/**
 * Step from the current selection to a neighbouring stop.
 *
 * Operates on the STOP LIST, so arrow keys move era-to-era like every other
 * interaction. A selection that is not itself a stop (a stale index left by an
 * axis reshape) steps to the neighbouring stop rather than refusing to move.
 * Returns null when there is nowhere to go.
 */
export function stepStop(
  stops: readonly number[],
  current: number,
  dir: 'prev' | 'next' | 'first' | 'last',
): number | null {
  if (stops.length === 0) return null;
  if (dir === 'first') return stops[0] ?? null;
  if (dir === 'last') return stops[stops.length - 1] ?? null;
  const at = stops.findIndex((s) => s >= current);
  const cur = at === -1 ? stops.length - 1 : at;
  const onStop = stops[cur] === current;
  const target = dir === 'prev' ? (onStop ? cur - 1 : cur) : cur + (onStop ? 1 : 0);
  return stops[Math.min(stops.length - 1, Math.max(0, target))] ?? null;
}
